import type { RefObject } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { LocationsTooltip } from "app/pages/digital-marketing/keywords-campaigns/shared/components";
import type { LocationInfo } from "app/pages/digital-marketing/keywords-campaigns/shared/components";

import { importSourceIconNameMap } from "./constants";
import {
    CloseButton,
    ImportSourceContainer,
    ImportSourceTextContainer,
    StyledImportSourceIcon,
    TextWrapper,
    Truncate,
    LocationsBadge,
} from "./styles";
import type { ImportSourceType } from "./types";

type Props = {
    ref?: RefObject<HTMLDivElement>;
    name: string;
    type: ImportSourceType;
    onRemove: () => void;
    keywordsAmount: number;
    hasError?: boolean;
    errorLabel?: string;
    locations?: LocationInfo[];
};

const ImportSourceIcon = ({ type, hasError }: { type: Props["type"]; hasError?: boolean }) => {
    return (
        <StyledImportSourceIcon
            type={type}
            hasError={hasError}
        >
            <SWReactIcons
                iconName={hasError ? "error-info" : importSourceIconNameMap[type]}
                size="sm"
                iconColor={colorsPalettes.carbon[0]}
            />
        </StyledImportSourceIcon>
    );
};

export const ImportSource = ({
    ref,
    name,
    type,
    onRemove,
    keywordsAmount,
    hasError,
    errorLabel = "Default error label",
    locations,
}: Props) => {
    const { translate } = useTranslate();
    return (
        <ImportSourceContainer ref={ref}>
            <ImportSourceIcon
                type={type}
                hasError={hasError}
            />
            {locations ? (
                <LocationsTooltip locations={locations}>
                    <LocationsBadge>
                        <Text
                            fontVariant="caption2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {locations.length}
                        </Text>
                    </LocationsBadge>
                </LocationsTooltip>
            ) : null}

            <ImportSourceTextContainer>
                <TextWrapper>
                    <Truncate>
                        <Text
                            overflow="hidden"
                            fontVariant="body2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {name}
                        </Text>
                    </Truncate>
                </TextWrapper>
                <Text
                    fontVariant="listItem3"
                    color={hasError ? colorsPalettes.red[400] : colorsPalettes.carbon[300]}
                >
                    {hasError
                        ? errorLabel
                        : translate("campaigns.keywordsStep.importSource.keywordsCount", { keywordsAmount })}
                </Text>
            </ImportSourceTextContainer>

            <CloseButton onClick={onRemove}>
                <SWReactIcons
                    iconName="clear-circle"
                    size="sm"
                    iconColor={colorsPalettes.carbon[500]}
                />
            </CloseButton>
        </ImportSourceContainer>
    );
};
